import { computed, ref, watch, type Ref } from 'vue'
import type {
  AgreementDocument,
  CreateOrganizationRequest,
  PublishedAgreement
} from '@/api/generated-organizations'
import { getGlobalT } from '@/i18n'
import { initApiService } from '@/services/init'
import { isValidAgbCode } from '@/utils/agbCode'
import { isValidBtwNumber, normalizedBtwNumber } from '@/utils/btwNumber'
import { isValidEmail } from '@/utils/email'
import { getApiErrorCode, getAxiosStatus } from '@/utils/errorHandling'
import { normalizedKvkNumber } from '@/utils/kvkNumber'
import { canonicalizePhoneNumber } from '@/utils/phoneNumber'

/**
 * The questions a practice signing itself up can actually answer, as the strings
 * a `v-model` binds. {@link OrganizationOnboardingFields.name} and
 * {@link OrganizationOnboardingFields.phone} are required — the phone because a
 * practice that signs itself up is one Scrivio has not spoken to yet (#4894). Every
 * other answer may be left empty and filled in later in Organisatiebeheer, which is
 * what the form says out loud (#4437).
 *
 * The last four are what a Scrivio invoice to this practice is addressed to, and
 * they are asked here because signing up is where a practice describes itself.
 * `organizations` grew a column for each in #4448; before that they were
 * deliberately absent, since a field filled in and then dropped is worse than one
 * never offered.
 */
export interface OrganizationOnboardingFields {
  name: string
  phone: string
  kvkNumber: string
  agbCode: string
  addressStreet: string
  addressPostalCode: string
  addressCity: string
  email: string
  statutoryName: string
  billingContactName: string
  billingEmail: string
  btwNumber: string
}

/** Per answer, plus the one thing the form asks that is not a typed answer (#4490). */
export type OrganizationOnboardingErrors = Partial<
  Record<keyof OrganizationOnboardingFields | 'agreements', string>
>

/** Where an in-progress form is parked while the user is somewhere else. */
const STORAGE_KEY = 'scrivio_organization_onboarding'

/** Dutch postcode, with or without the space: `1234 AB` / `1234AB`. */
const POSTAL_CODE_PATTERN = /^\d{4}\s?[A-Z]{2}$/i

const emptyFields = (): OrganizationOnboardingFields => ({
  name: '',
  phone: '',
  kvkNumber: '',
  agbCode: '',
  addressStreet: '',
  addressPostalCode: '',
  addressCity: '',
  email: '',
  statutoryName: '',
  billingContactName: '',
  billingEmail: '',
  btwNumber: ''
})

/** One stored answer, or the empty one when it is missing or not a string. */
function readString(source: Record<string, unknown>, key: keyof OrganizationOnboardingFields) {
  const value = source[key]
  return typeof value === 'string' ? value : ''
}

interface StoredOnboarding {
  open: boolean
  fields: OrganizationOnboardingFields
}

/**
 * Read the parked flow back.
 *
 * Every field is rebuilt from {@link emptyFields} rather than spread from
 * whatever was parsed, so a record written by another build — or hand-edited —
 * can only ever contribute strings to the fields this build knows.
 */
function load(): StoredOnboarding {
  const fresh: StoredOnboarding = { open: false, fields: emptyFields() }
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return fresh

    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return fresh

    const record = parsed as Record<string, unknown>
    const storedFields =
      typeof record.fields === 'object' && record.fields !== null
        ? (record.fields as Record<string, unknown>)
        : {}

    const fields = emptyFields()
    for (const key of Object.keys(fields) as (keyof OrganizationOnboardingFields)[]) {
      fields[key] = readString(storedFields, key)
    }
    return { open: record.open === true, fields }
  } catch {
    // Unavailable, full, or holding something this build cannot read. Starting
    // from an empty form is the whole fallback: the persistence is a per-viewer
    // convenience, never the record of anything.
    return fresh
  }
}

/**
 * Persist the flow, or drop it when there is nothing to remember.
 *
 * A closed form with every answer blank is exactly the state a first-time
 * visitor starts in, so storing it would leave a record of a flow nobody is in.
 * Removing it here rather than in {@link useOrganizationOnboarding}'s `reset` is
 * what makes that reliable: a `removeItem` there would be undone by this
 * watcher-driven write on the very next flush.
 */
function save(state: StoredOnboarding): void {
  try {
    if (!state.open && !Object.values(state.fields).some((answer) => answer !== '')) {
      sessionStorage.removeItem(STORAGE_KEY)
      return
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // sessionStorage unavailable or full — the form still works, it just will
    // not survive a reload.
  }
}

/** Which of the two identifiers an organisation in Scrivio already carries. */
interface OrganizationIdentifiersInUse {
  kvkNumber: boolean
  agbCode: boolean
}

const NOTHING_IN_USE: OrganizationIdentifiersInUse = { kvkNumber: false, agbCode: false }

/**
 * How long a field rests before it is asked about. Long enough that typing an eight-digit
 * number is one question rather than eight, short enough that the warning is still an answer
 * to what the user just did.
 */
const LOOKUP_DELAY_MS = 400

interface OnboardingState {
  fields: Ref<OrganizationOnboardingFields>
  errors: Ref<OrganizationOnboardingErrors>
  /** Fields the user has left, so an error only shows once it can be acted on. */
  touched: Ref<Set<keyof OrganizationOnboardingFields>>
  isOpen: Ref<boolean>
  /** What the backend last answered about the identifiers as they now stand. */
  inUse: Ref<OrganizationIdentifiersInUse>
  /** Each agreement as currently published, as the backend states it — never typed here. */
  agreements: Ref<PublishedAgreement[]>
  /**
   * Whether the practice accepted {@link agreements} as they now stand. Deliberately not
   * in the stored draft: an acceptance is of the versions on screen, and a tick restored
   * from `sessionStorage` would be one nobody gave to what is loaded now.
   */
  agreementsAccepted: Ref<boolean>
  /** The last load failed, so there is nothing to link and nothing to accept. */
  agreementsUnavailable: Ref<boolean>
  /** A version was published while the form was open, and the tick was withdrawn for it. */
  agreementsOutdated: Ref<boolean>
}

/**
 * The AGB-code as it would be asked about, or null when there is nothing to ask.
 *
 * A blank field is not a question, and a malformed one is not either — the contract's own
 * `pattern` would refuse it, and a 400 is not something to show a practice that is halfway
 * through typing.
 */
function askableAgbCode(value: string): string | null {
  const trimmed = value.trim()
  return trimmed !== '' && isValidAgbCode(trimmed) ? trimmed : null
}

/**
 * Ask, after a pause, whether the practice being typed in is already in Scrivio, and keep
 * {@link OnboardingState.inUse} answering for the values as they now stand (#4438).
 *
 * Registered once, from {@link stateOnce}, beside the persistence watcher — the state is shared
 * by the form and the dashboard, and two copies of this would double every request.
 *
 * Three properties are load-bearing:
 *
 * - **An unasked question has no answer.** A field that goes blank — or malformed — drops its
 *   warning at once rather than leaving the previous one standing over a value it was never
 *   about.
 * - **A late answer is discarded.** Each question carries a sequence number, so an earlier
 *   request that resolves after a later one cannot overwrite it with an answer about a value
 *   the user has already moved on from.
 * - **A failed lookup warns about nothing.** This is advisory; creation does not consult it and
 *   refuses nothing. An offline browser, a 429 or a 500 therefore leaves the form exactly as
 *   usable as it was, silently.
 *
 * @returns a function that stops the watcher and discards whatever is in flight.
 */
function watchForExistingPractice(state: OnboardingState): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined
  let asked = 0

  /**
   * Put one question and record its answer, unless it has been overtaken.
   *
   * The whole call is inside the `try`, `initApiService()` included: this runs from a timer,
   * so anything thrown here would surface as an uncaught error in a flow that is meant to be
   * invisible when it fails.
   */
  async function ask(question: number, kvkNumber: string | null, agbCode: string | null) {
    let answer: OrganizationIdentifiersInUse
    try {
      const { data } = await initApiService().organizations.checkOrganizationIdentifiers({
        kvkNumber,
        agbCode
      })
      answer = { kvkNumber: data.kvkNumberInUse, agbCode: data.agbCodeInUse }
    } catch {
      answer = { ...NOTHING_IN_USE }
    }

    if (question === asked) state.inUse.value = answer
  }

  const stop = watch(
    [
      () => normalizedKvkNumber(state.fields.value.kvkNumber),
      () => askableAgbCode(state.fields.value.agbCode)
    ] as const,
    ([kvkNumber, agbCode]) => {
      if (timer !== undefined) clearTimeout(timer)

      state.inUse.value = {
        kvkNumber: kvkNumber === null ? false : state.inUse.value.kvkNumber,
        agbCode: agbCode === null ? false : state.inUse.value.agbCode
      }
      if (kvkNumber === null && agbCode === null) return

      const question = ++asked
      timer = setTimeout(() => {
        void ask(question, kvkNumber, agbCode)
      }, LOOKUP_DELAY_MS)
    },
    // A parked draft comes back with its answers already filled in, so the question it
    // poses is asked on restore rather than on the next keystroke. An empty form returns
    // above without asking anything.
    { immediate: true }
  )

  return () => {
    if (timer !== undefined) clearTimeout(timer)
    // Past the last question, so an answer already in flight is discarded rather than
    // written into state nothing is reading any more.
    asked++
    stop()
  }
}

/**
 * Shared across the page: `DashboardView` decides *whether* the form shows and
 * `OrganizationOnboardingForm` fills it in, and both have to be looking at the
 * same flow rather than at two copies of it. Module-level is also what makes the
 * flow survive a navigation — the component unmounts, the state does not.
 */
let sharedState: OnboardingState | null = null

/** Stops the duplicate-lookup watcher that belongs to {@link sharedState}. */
let stopLookup: (() => void) | null = null

function stateOnce(): OnboardingState {
  if (sharedState) return sharedState

  const stored = load()
  const state: OnboardingState = {
    fields: ref(stored.fields),
    errors: ref({}),
    touched: ref(new Set()),
    isOpen: ref(stored.open),
    inUse: ref({ ...NOTHING_IN_USE }),
    agreements: ref([]),
    agreementsAccepted: ref(false),
    agreementsUnavailable: ref(false),
    agreementsOutdated: ref(false)
  }
  watch([state.fields, state.isOpen] as const, ([fields, open]) => save({ open, fields }), {
    deep: true
  })
  stopLookup = watchForExistingPractice(state)

  sharedState = state
  return state
}

/**
 * The state of "I am creating my own practice", and the one place it is
 * validated and persisted.
 *
 * The flow is **escapable**, which is the half of #4437 that is not a field
 * list: a user may leave the form for any other screen and come back to exactly
 * what they had typed, and nothing anywhere holds them inside it. The answers
 * live in `sessionStorage` for that, in a try/catch on both sides — it is a
 * per-viewer convenience, so a browser that refuses it renders a correct empty
 * form rather than an error.
 *
 * The AGB-code and the BTW-nummer validate live, on blur. They are the two
 * answers the backend itself refuses — `pattern: '^\d{8}$'` on
 * `CreateOrganizationRequest` and `BtwNumber.normalize` behind it — so a malformed
 * one comes back as a 400 naming a field the practice has never heard of, instead
 * of the inline message it can act on. Each format is asked through its own module
 * ({@link isValidAgbCode}, {@link isValidBtwNumber}) rather than a regex here, which
 * is what `billing:agb-format` exists to keep true for the first of them (#4275).
 * The rest is checked on submit, where an error answers something the user just did.
 *
 * The two duplicate warnings are a different kind of thing from either: they say the
 * practice appears to be in Scrivio already and that the way in is an invitation from its
 * beheerder, and they hold nothing down. A second organisation under one KvK is legitimate,
 * so a block would make a real case unreachable; a practice that means it proceeds, one that
 * did not, stops (#4438).
 *
 * Last, the practice accepts the Overeenkomst and the Verwerkersovereenkomst (#4490).
 * What it accepts is read from the backend — the version of each and the page it is
 * published on — and sent straight back, so no version or link is typed here and a
 * publication moves the links and the gate at once. A form left open across a
 * publication is refused by the backend; {@link askAgreementsAgain} is the answer to
 * that, and everything typed stays where it was.
 */
export function useOrganizationOnboarding() {
  const t = getGlobalT()

  const {
    fields,
    errors,
    touched,
    isOpen,
    inUse,
    agreements,
    agreementsAccepted,
    agreementsUnavailable,
    agreementsOutdated
  } = stateOnce()

  const isAgbCodeValid = computed(() => isValidAgbCode(fields.value.agbCode))
  const isBtwNumberValid = computed(() => isValidBtwNumber(fields.value.btwNumber))

  /** The one message the AGB field shows, whether it came from blur or from submit. */
  const agbCodeError = computed(() => {
    if (errors.value.agbCode) return errors.value.agbCode
    if (touched.value.has('agbCode') && !isAgbCodeValid.value) {
      return t('validation.agbCodeFormat')
    }
    return undefined
  })

  /** The same, for the BTW-nummer. */
  const btwNumberError = computed(() => {
    if (errors.value.btwNumber) return errors.value.btwNumber
    if (touched.value.has('btwNumber') && !isBtwNumberValid.value) {
      return t('validation.btwNumberFormat')
    }
    return undefined
  })

  /**
   * A malformed AGB-code or BTW-nummer is what holds the submit button down: both
   * are refused by the backend, so submitting one buys a 400 rather than an
   * organisation. The practice name being empty is caught by {@link validate}
   * instead, which can say *which* field is missing — a disabled button on an empty
   * form says nothing at all.
   */
  const canSubmit = computed(() => isAgbCodeValid.value && isBtwNumberValid.value)

  /**
   * Advisory, and deliberately not folded into {@link agbCodeError}: an error is the form
   * refusing an answer, and these two refuse nothing. Both render beside the field they are
   * about rather than as a form-level alert, because which identifier matched is the whole
   * content of the message.
   */
  const kvkNumberWarning = computed(() =>
    inUse.value.kvkNumber ? t('forms.organization.onboarding.kvkNumberInUse') : undefined
  )

  const agbCodeWarning = computed(() =>
    inUse.value.agbCode ? t('forms.organization.onboarding.agbCodeInUse') : undefined
  )

  /** Where each document is published, for the links beside the checkbox. */
  const agreementUrls = computed(
    () =>
      Object.fromEntries(agreements.value.map(({ document, url }) => [document, url])) as Partial<
        Record<AgreementDocument, string>
      >
  )

  /**
   * Read what there is to accept. The form calls this each time it mounts, so a form
   * reopened after a publication links the new text — and a version that moved withdraws
   * the tick, because what was accepted is no longer what would be sent.
   */
  async function loadAgreements(): Promise<void> {
    try {
      const { data } = await initApiService().organizations.getPublishedAgreements()
      if (!sameVersions(agreements.value, data)) agreementsAccepted.value = false
      agreements.value = data
      agreementsUnavailable.value = false
    } catch {
      agreements.value = []
      agreementsAccepted.value = false
      agreementsUnavailable.value = true
    }
  }

  function setAgreementsAccepted(accepted: boolean): void {
    agreementsAccepted.value = accepted
    if (!accepted) return
    agreementsOutdated.value = false
    const answered = { ...errors.value }
    delete answered.agreements
    errors.value = answered
  }

  /**
   * The backend refused the create request over the agreements: a new version was
   * published while the form was open. Withdraw the tick, say why, and fetch what is
   * current — the rest of the form is untouched, so accepting again is the whole cost.
   */
  async function askAgreementsAgain(): Promise<void> {
    agreementsAccepted.value = false
    agreementsOutdated.value = true
    await loadAgreements()
  }

  function markTouched(field: keyof OrganizationOnboardingFields): void {
    touched.value = new Set(touched.value).add(field)
  }

  function open(): void {
    isOpen.value = true
  }

  /** Leave the form without losing it — the "escapable" half of the flow. */
  function close(): void {
    isOpen.value = false
  }

  /** Forget the whole flow. Called once the organisation actually exists. */
  function reset(): void {
    fields.value = emptyFields()
    errors.value = {}
    touched.value = new Set()
    isOpen.value = false
    agreementsAccepted.value = false
    agreementsOutdated.value = false
  }

  function validate(): boolean {
    const found: OrganizationOnboardingErrors = {}

    if (!fields.value.name.trim()) {
      found.name = t('forms.organization.validation.nameRequired')
    }
    // Required, so blank is refused here rather than accepted as "not provided" the way
    // `isValidPhoneNumber` reads it elsewhere: canonicalising is both checks at once.
    if (!fields.value.phone.trim()) {
      found.phone = t('forms.organization.validation.phoneRequired')
    } else if (canonicalizePhoneNumber(fields.value.phone) === null) {
      found.phone = t('validation.phoneFormat')
    }
    if (!isValidEmail(fields.value.email)) {
      found.email = t('forms.organization.validation.invalidEmail')
    }
    if (!isValidEmail(fields.value.billingEmail)) {
      found.billingEmail = t('forms.organization.validation.invalidEmail')
    }
    if (!isBtwNumberValid.value) {
      found.btwNumber = t('validation.btwNumberFormat')
    }
    if (
      fields.value.addressPostalCode &&
      !POSTAL_CODE_PATTERN.test(fields.value.addressPostalCode)
    ) {
      found.addressPostalCode = t('forms.organization.validation.invalidPostalCode')
    }
    if (!isAgbCodeValid.value) {
      found.agbCode = t('validation.agbCodeFormat')
    }
    // Nothing loaded is nothing accepted: an empty list would reach the backend as a
    // request that agreed to nothing, which it refuses.
    if (!agreementsAccepted.value || agreements.value.length === 0) {
      found.agreements = t('forms.organization.onboarding.agreements.required')
    }

    errors.value = found
    return Object.keys(found).length === 0
  }

  /**
   * What the API is asked for.
   *
   * No `slug`: it is derived from the name and de-duplicated by the backend
   * (#4434), so sending one would be a value that is accepted and ignored. An
   * answer left empty travels as `null` rather than as `''` — an empty string is
   * a stored answer, and "not filled in yet" is what the user meant.
   *
   * The BTW-nummer and the phone number travel in their canonical form. The backend
   * normalises both either way, so sending what was typed would store the same value —
   * but then what the practice sees back in Organisatiebeheer is not what it just
   * submitted. {@link validate} has already refused a phone that has no canonical form.
   *
   * The agreements travel as the versions the backend published and the practice
   * accepted — never a version of this build's own (#4490).
   */
  function buildRequest(): CreateOrganizationRequest {
    const trimmed = (value: string) => value.trim() || null

    return {
      acceptedAgreements: agreements.value.map(({ document, version }) => ({ document, version })),
      name: fields.value.name.trim(),
      phone: canonicalizePhoneNumber(fields.value.phone) ?? fields.value.phone.trim(),
      kvkNumber: trimmed(fields.value.kvkNumber),
      agbCode: trimmed(fields.value.agbCode),
      addressStreet: trimmed(fields.value.addressStreet),
      addressPostalCode: trimmed(fields.value.addressPostalCode),
      addressCity: trimmed(fields.value.addressCity),
      email: trimmed(fields.value.email),
      statutoryName: trimmed(fields.value.statutoryName),
      billingContactName: trimmed(fields.value.billingContactName),
      billingEmail: trimmed(fields.value.billingEmail),
      btwNumber: normalizedBtwNumber(fields.value.btwNumber)
    }
  }

  return {
    fields,
    errors,
    isOpen,
    agbCodeError,
    btwNumberError,
    kvkNumberWarning,
    agbCodeWarning,
    canSubmit,
    agreements,
    agreementUrls,
    agreementsAccepted,
    agreementsUnavailable,
    agreementsOutdated,
    loadAgreements,
    setAgreementsAccepted,
    askAgreementsAgain,
    markTouched,
    open,
    close,
    reset,
    validate,
    buildRequest
  }
}

/** Whether two lists name the same version of each document. */
function sameVersions(a: PublishedAgreement[], b: PublishedAgreement[]): boolean {
  return (
    a.length === b.length &&
    a.every((x) => b.some((y) => y.document === x.document && y.version === x.version))
  )
}

/** The RFC 7807 `code` a signup is refused with when it did not accept what is current. */
const AGREEMENT_ACCEPTANCE_INVALID = 'agreement-acceptance-invalid'

/**
 * Whether a failed create request was refused over the agreements — the one 400 the form
 * answers by asking again ({@link useOrganizationOnboarding}'s `askAgreementsAgain`)
 * rather than by showing a message.
 */
export function isAgreementAcceptanceInvalid(err: unknown): boolean {
  return getAxiosStatus(err) === 400 && getApiErrorCode(err) === AGREEMENT_ACCEPTANCE_INVALID
}

/**
 * Test-only: forget the shared state so the next call re-reads `sessionStorage`.
 *
 * The duplicate lookup is stopped as well. Without that, a debounce timer armed by one test
 * fires during the next one, into a state object nothing is reading — a request no test asked
 * for, answered into the void.
 */
export function resetOrganizationOnboardingForTests(): void {
  stopLookup?.()
  stopLookup = null
  sharedState = null
}
